/** Kinematic data upload page */
import { IonContent, IonGrid, IonPage, IonRow } from '@ionic/react';
// Components
import { useEffect, useMemo, useState } from 'react';
import { useLocation, useParams } from 'react-router';

import { Button } from '@/components/Button';
import { ClientOverviewHeader } from '@/components/ClientHeaders';
import { EmptySessionState } from '@/components/Clients/EmptySessionState';
import { CreateSessionModalContent } from '@/components/GOSession/CreateSessionModalContent';
import { CustomLoading } from '@/components/Loading/CustomLoading';
import { Modal } from '@/components/Modal/Modal';
import { SessionSection } from '@/components/SessionSection/SessionSection';

import SessionManager from '@/hooks/useClients/sessionManager';

import { useClientContext, useSessionContext } from '@/contexts';
import { useAuthContext } from '@/contexts/authContext';

// Hooks
import { Session } from '@/models';

// Styles
import styles from '@/pages/Clients/Clients.module.css';

interface ParamTypes {
  id: string;
}

interface LocationState {
  clientName?: string;
  clientId?: string;
  resetActiveSession?: boolean;
  measurementType: string;
}

// TODO: Change button onclick in Empty state to add Session
// TODO: On session creation, redirect to session page with loading state
// TODO: Fetch hardware on session creation
// ! Does not work correctly on Ipad (Doesn't open the first session, and opens the wrong session on click)
export const ClientsPage = () => {
  const { id } = useParams<ParamTypes>();
  const location = useLocation<LocationState>();
  const user = useAuthContext().state.userState?.user;
  const { setClientName, clientName, setClientId, clientId, activeSessionId, setActiveSessionId } = useClientContext();
  const { sessionsList, setSessionsList, isLoading, setIsLoading, error, setError } = useSessionContext();
  const [isFirstLoad, setIsFirstLoad] = useState(true);

  useEffect(() => {
    const { clientName: locationClientName, clientId: locationClientId } = location.state || {};

    if (locationClientName) {
      sessionStorage.setItem('clientName', locationClientName);
      setClientName(locationClientName);
    }

    const newClientId = locationClientId || id;
    if (newClientId) {
      sessionStorage.setItem('clientId', newClientId);
      setClientId(newClientId);
    }
  }, [location.state, id, setClientName, setClientId]);

  const Name = useMemo(
    () => location.state?.clientName || sessionStorage.getItem('clientName') || clientName,
    [location.state?.clientName, clientName],
  );

  const clientID = useMemo(
    () => location.state?.clientId || sessionStorage.getItem('clientId') || clientId,
    [location.state?.clientId, clientId],
  );

  useEffect(() => {
    // TODO: Add error state
    if (!user?.uid || !clientID) return;

    // If sessions are already in context, don't fetch again
    if (sessionsList.length > 0) {
      console.log('Sessions already loaded from storage, skipping fetch.');
      setIsLoading(false);
      return;
    }

    console.log('Fetching sessions from Firestore...');

    setIsLoading(true); // Explicitly set loading state
    const unsubscribe = SessionManager.getSessions(user.uid, clientID, (sessions, error) => {
      if (error) {
        console.error('Error fetching sessions:', error);
        setError(error.message);
        setIsLoading(false);
        return;
      }

      if (sessions.length > 0) {
        setSessionsList(sessions);
      }

      setIsLoading(false);
    });

    return () => {
      if (unsubscribe) unsubscribe();
    };
  }, [user?.uid, clientID, setSessionsList, setIsLoading, setError]);

  useEffect(() => {
    if (sessionsList.length > 0) {
      if (isFirstLoad && location.state?.resetActiveSession) {
        console.log('Resetting active session');
        setActiveSessionId(sessionsList[0].id);
        setIsFirstLoad(false);
      } else if (!activeSessionId) {
        console.log('Setting active session');
        setActiveSessionId(sessionsList[0].id);
      } else {
        setActiveSessionId(activeSessionId);
        console.log('Active session already set');
        console.log('Sessions:', sessionsList);
      }
    }
  }, [sessionsList, activeSessionId, setActiveSessionId, location.state, isFirstLoad]);

  const handleToggleSession = (sessionId: string) => {
    setActiveSessionId(sessionId === activeSessionId ? activeSessionId : sessionId);
  };

  // Session data
  const [isAddSessionModalOpen, setIsAddSessionModalOpen] = useState(false);
  const [sessionID, setSessionID] = useState('');
  const [creatingSession, setCreatingSession] = useState(false);
  const [sessionData, setSessionData] = useState({
    date: new Date().toISOString(),
    clientId: clientId!,
    therapistId: user!.uid,
  });
  const [success, setSuccess] = useState('');

  const handleSessionInputChange = (field: string, value: string) => {
    setSessionData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSessionConfirm = async () => {
    setCreatingSession(true);
    console.log('Session Data:', sessionData);
    try {
      setSessionID(await SessionManager.createSession(sessionData));
      console.log('Session Created successfully');
      setIsAddSessionModalOpen(false);
      setSessionData({ date: new Date().toISOString(), clientId: '', therapistId: user!.uid });
      setSuccess('Session added successfully');
    } catch (error: any) {
      console.log('Error creating session', error);
    } finally {
      setCreatingSession(false);
    }
  };

  useEffect(() => {
    console.log('Session ID:', sessionID);
  }, [sessionID]);

  return (
    <>
      <Modal
        footer={
          <>
            <Button onClick={() => setIsAddSessionModalOpen(false)} variant='secondary'>
              Cancel
            </Button>
            <Button onClick={() => handleSessionConfirm()} variant='primary'>
              Confirm
            </Button>
          </>
        }
        header='Add new session'
        isOpen={isAddSessionModalOpen}
        onDidDismiss={() => setIsAddSessionModalOpen(false)}>
        <CreateSessionModalContent
          loading={creatingSession}
          onInputChange={handleSessionInputChange}
          sessionData={sessionData}
        />
      </Modal>
      <IonPage id='main'>
        <IonContent className={styles.content} color='light' id='main'>
          <div className={styles.header}>
            <ClientOverviewHeader backButton={true} backTarget='/clients' title={Name!}>
              <Button leadingIcon='plus' onClick={() => setIsAddSessionModalOpen(true)} size='medium' variant='primary'>
                Start new session
              </Button>
            </ClientOverviewHeader>
          </div>
          <IonGrid className={styles.grid} fixed>
            {isLoading && (
              <>
                <IonRow
                  className='ion-align-items-center ion-justify-content-center ion-margin-vertical'
                  style={{ width: '100%', height: '100%' }}>
                  <CustomLoading message={'Loading Sessions...'} />
                </IonRow>
              </>
            )}
            {!isLoading && (sessionsList.length === 0 || sessionsList.every((s) => !s.id)) && (
              <>
                <IonGrid className={styles.grid} fixed>
                  <EmptySessionState onClickEvent={() => console.log('Add new session')} />
                </IonGrid>
              </>
            )}

            {sessionsList.reverse().map((session, index) => (
              <SessionSection
                isActive={session.id === activeSessionId}
                key={index}
                onToggle={() => handleToggleSession(session.id)}
                session={session}
                sessionIndex={index}
              />
            ))}
          </IonGrid>
        </IonContent>
      </IonPage>
    </>
  );
};
